/**
 * 用語の備考の入力欄 — 人が画面からだけ書ける欄（AI は書けない）。
 *
 * 案件を選んでいるときだけ出す。用語の変更は案件に積んで承認を通すので、案件の外では書く先が
 * 無い。どの要求のための変更かを選ばせ（案件の変更は要求を 1 つ持つ）、保存すると用語の変更が
 * 承認待ちで積まれる。
 *
 * 拒否は API が返した文言をそのまま出す。入口で形の判定を複製すると、共通部の規則が変わった
 * ときに画面だけが黙ってずれる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件を選んでいるとき、用語の備考を要求を選んで書き、承認待ちの変更として案件へ積む。
 * @graph-connects usePjRequirements [calls] 案件の要求の取得
 * @graph-connects useUpdateTermNote [calls] 用語の備考の書込
 */
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import { Textarea } from '@cortex/web-design-system/components/ui/Textarea';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { usePjRequirements } from '../hooks/queries/spec-graph-element-pj-queries.js';
import { useUpdateTermNote } from '../hooks/queries/spec-pj-element-queries.js';

/** `TermNoteEditor` の props。 @graph-connects none */
interface TermNoteEditorProps {
  /** 用語の番号 */
  termId: number;
  /** いま重ねている案件番号 */
  pjNumber: number;
  /** 今の備考（無ければ null） */
  note: string | null;
}

/**
 * 用語の備考の入力欄。要求の選択・本文・保存を 1 つの枠に置く。
 *
 * @param props 用語の番号・案件番号・今の備考
 * @returns 入力欄
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語の備考を、要求を選んで書き、保存で案件へ積み、結果（承認待ち・拒否の文言）を出す。
 * @graph-connects usePjRequirements [calls] 案件の要求の取得
 * @graph-connects useUpdateTermNote [calls] 用語の備考の書込
 */
export function TermNoteEditor({ termId, pjNumber, note }: TermNoteEditorProps) {
  const { t } = useTranslation();
  const requirements = usePjRequirements(pjNumber);
  const update = useUpdateTermNote();
  const [draft, setDraft] = useState(note ?? '');
  const [requirementId, setRequirementId] = useState('');
  const options = (requirements.data?.requirements ?? []).map((requirement) => ({
    value: String(requirement.requirementId),
    label: `#${String(requirement.requirementId)} ${requirement.statement}`,
  }));
  const result = update.data;
  const trimmed = draft.trim();
  /* 消すのは今の備考があって本文を空にしたときだけ。備考が無いまま空なら送るものが無い。 */
  const clearing = trimmed === '' && note !== null;
  const nothingToSend = trimmed === '' && note === null;

  return (
    <section
      className="flex flex-col gap-2 rounded border border-[var(--color-border)] p-3"
      data-term-note-editor
    >
      <h2 className="text-sm font-bold text-[var(--color-text-primary)]">
        {t('termDetail.noteEditTitle')}
      </h2>
      <p className="text-xs text-[var(--color-text-secondary)]">{t('termDetail.noteEditHint')}</p>
      <Select
        aria-label={t('termDetail.noteRequirement')}
        placeholder={t('termDetail.noteRequirement')}
        options={options}
        value={requirementId}
        onChange={(event) => {
          setRequirementId(event.target.value);
        }}
      />
      <Textarea
        rows={8}
        value={draft}
        aria-label={t('termDetail.noteTitle')}
        onChange={(event) => {
          setDraft(event.target.value);
        }}
      />
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="primary"
          size="sm"
          disabled={requirementId === '' || nothingToSend || update.isPending}
          onClick={() => {
            update.mutate({
              pjNumber,
              requirementId: Number(requirementId),
              termId,
              note: clearing ? null : draft,
            });
          }}
        >
          {clearing ? t('termDetail.noteClear') : t('termDetail.noteSave')}
        </Button>
        {result?.outcome === 'applied' && (
          <span className="text-xs text-[var(--color-text-secondary)]" role="status">
            {t('termDetail.noteSaved')}
          </span>
        )}
        {result?.outcome === 'rejected' && (
          <span className="text-xs text-[var(--color-error)]" role="alert">
            {result.rejections.map((rejection) => rejection.message).join(' / ')}
          </span>
        )}
        {update.error !== null && (
          <span className="text-xs text-[var(--color-error)]" role="alert">
            {update.error.message}
          </span>
        )}
      </div>
    </section>
  );
}
